// src/utils/jwtDecoder.ts

import type { DecodedJWT, JwtBaseClaims } from "@/types/jwt.types";

const MIN = 0;
const PAD_LEN = 4
const JWT_LEN = 3

/** Base64-url decode (browser) */
function base64UrlDecode(str: string): string {
  // Convert URL-safe Base64 to standard Base64
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  // Pad with '=' to multiple of 4
  const padded = base64.padEnd(base64.length + ((PAD_LEN - (base64.length % PAD_LEN)) % PAD_LEN), "=");
  try {
    return atob(padded);
  } catch {
    throw new Error("Invalid base64 string in JWT");
  }
}

/**
 * Decode a JWT (no signature verification).
 * @param token JWT string
 * @returns Decoded header, payload, signature
 */
export function decodeJWT<T extends Record<string, unknown> = Record<string, unknown>>(
  token: string
): DecodedJWT<T> {
  if ((token.length === MIN) || typeof token !== "string") {
    throw new Error("Invalid token provided");
  }

  const parts = token.split(".");
  if (parts.length !== JWT_LEN) throw new Error("Invalid JWT format");

  const [headerB64, payloadB64, signature] = parts;

  let headerJson: unknown;
  let payloadJson: unknown;

  try {
    headerJson = JSON.parse(base64UrlDecode(headerB64));
  } catch (e: unknown) {
     if (e instanceof Error) {
    throw new Error('Failed to decode JWT header: ' + e.message);
  }
  throw new Error('Failed to decode JWT header: Unknown error');
  }

  try {
    payloadJson = JSON.parse(base64UrlDecode(payloadB64));
  } catch (e: unknown) {
    if (e instanceof Error) {
    throw new Error('Failed to decode JWT header: ' + e.message);
  }
  throw new Error('Failed to decode JWT header: Unknown error');
  }

  return {
    header: (headerJson ?? {}) as Record<string, unknown>,
    // ✅ explicitly assert the intersection type T & JwtBaseClaims
    payload: (payloadJson ?? {}) as T & JwtBaseClaims,
    signature,
  };
}

/**
 * Check if a JWT is expired (uses `exp` claim if present).
 * Returns true on invalid tokens as a safe default.
 */
export function isJWTExpired(token: string): boolean {
  try {
    const { payload } = decodeJWT(token);
    if (typeof payload.exp !== "number") return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return payload.exp < nowSec;
  } catch {
    return true;
  }
}

/**
 * Get a specific claim (typed) from a JWT payload.
 * Returns undefined if token is invalid or claim missing.
 */
export function getJwtClaim<K extends string, V = unknown>(
  token: string,
  claim: K
): V | undefined {
  try {
    const { payload } = decodeJWT<Record<string, V>>(token);
    return payload[claim];
  } catch {
    return undefined;
  }
}
